import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ChatApp, LoginScreen } from './components';
import { ProfilePage } from './components/ProfilePage';
import { AdminDashboard } from './components/AdminDashboard';
import { MaintenancePage } from './components/MaintenancePage';
import { COGNITO_DOMAIN, COGNITO_CLIENT_ID } from './utils/constants';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { apiFetch } from './utils/api';
import './App.css';

// Capture auth code BEFORE React mounts (router may strip it)
const initialUrlParams = new URLSearchParams(window.location.search);
let initialAuthCode: string | null = initialUrlParams.get('code');
if (initialAuthCode) {
  // Clean URL immediately to prevent double-use
  window.history.replaceState({}, document.title, window.location.pathname);
}

const getInitialAuth = () => {
  const savedToken = localStorage.getItem('cognito_token');
  const savedEmail = localStorage.getItem('user_email');
  
  return {
    token: savedToken,
    email: savedEmail || '',
    isLoading: !!initialAuthCode
  };
};

const ProtectedRoute: React.FC<{ children: React.ReactElement; isAuthenticated: boolean }> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

const AdminRoute: React.FC<{ children: React.ReactElement; isAdmin: boolean }> = ({ children, isAdmin }) => {
  return isAdmin ? children : <Navigate to="/" replace />;
};

function AppContent() {
  const navigate = useNavigate();
  const initialAuth = getInitialAuth();
  
  const [token, setToken] = useState<string | null>(initialAuth.token);
  const [userEmail, setUserEmail] = useState<string>(initialAuth.email);
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(initialAuth.isLoading);
  const [authChecked, setAuthChecked] = useState(!initialAuth.isLoading);

  const [isAdmin, setIsAdmin] = useState(() => {
    // Check admin status from existing token on load
    if (initialAuth.token) {
      try {
        const payload = JSON.parse(atob(initialAuth.token.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        return groups.includes('Admins');
      } catch {
        return false;
      }
    }
    return false;
  });

  useEffect(() => {
    // Check for callback from Cognito
    const code = initialAuthCode;
    
    if (code) {
      initialAuthCode = null; // Prevent double exchange in StrictMode
      exchangeCodeForToken(code);
    } else if (!authChecked) {
      setAuthChecked(true);
    }
    
    // Fetch user profile if token exists
    if (token && !userName) {
      fetchUserProfile();
    }
  }, [authChecked, token, userName]);

  const exchangeCodeForToken = async (code: string) => {
    try {
      const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: COGNITO_CLIENT_ID!,
          code: code,
          redirect_uri: window.location.origin + '/'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        console.error('Token exchange failed:', response.status, data);
        setIsLoading(false);
        setAuthChecked(true);
        return;
      }

      const idToken = data.id_token;
      const refreshToken = data.refresh_token;
      
      // Decode JWT to get user info
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const email = payload.email;
      
      localStorage.setItem('cognito_token', idToken);
      localStorage.setItem('user_email', email);
      if (refreshToken) {
        localStorage.setItem('cognito_refresh_token', refreshToken);
      }
      
      // Check if user is admin (check Cognito groups)
      const groups = payload['cognito:groups'] || [];
      const adminStatus = groups.includes('Admins');
      setIsAdmin(adminStatus);
      
      // Set all auth state together before clearing loading
      setToken(idToken);
      setUserEmail(email);
      setIsLoading(false);
      setAuthChecked(true);
    } catch (error) {
      console.error('Token exchange error:', error);
      setIsLoading(false);
      setAuthChecked(true);
    }
  };

  const fetchUserProfile = async () => {
    if (!token) return;
    try {
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Priority 1: Profile display name
        if (data.profile?.display_name && data.profile.display_name.trim()) {
          setUserName(data.profile.display_name);
        } else {
          // Priority 2: Cognito name attribute
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const cognitoName = payload.name || payload.given_name;
            if (cognitoName) {
              setUserName(cognitoName);
            }
          } catch {}
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleLogin = () => {
    const redirectUri = window.location.origin + '/';
    const loginUrl = `https://${COGNITO_DOMAIN}/login?client_id=${COGNITO_CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = loginUrl;
  };

  const handleMidwayLogin = () => {
    const redirectUri = window.location.origin + '/';
    const midwayUrl = `https://${COGNITO_DOMAIN}/oauth2/authorize?identity_provider=AmazonFederate&client_id=${COGNITO_CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = midwayUrl;
  };

  const handleTokenRefresh = (newToken: string) => {
    setToken(newToken);
    // Decode to update email and admin status
    try {
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      const email = payload.email;
      const groups = payload['cognito:groups'] || [];
      const adminStatus = groups.includes('Admins');
      localStorage.setItem('user_email', email);
      setUserEmail(email);
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error('Error decoding refreshed token:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cognito_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('user_email');
    setToken(null);
    setUserEmail('');
    window.location.reload();
  };

  // Use token refresh hook
  const { markActivity } = useTokenRefresh({
    token,
    onTokenRefresh: handleTokenRefresh,
    onLogout: handleLogout
  });

  // Mark activity every 2 minutes to prevent logout during long operations
  useEffect(() => {
    const interval = setInterval(() => {
      markActivity();
    }, 2 * 60 * 1000); // Every 2 minutes
    
    return () => clearInterval(interval);
  }, [markActivity]);

  // Show loading screen during auth flow until token is set
  if ((isLoading || !authChecked || initialAuthCode) && !(token && userEmail)) {
    return <div className="loading-screen">Loading...</div>;
  }

  // Render routes
  if (token && userEmail) {
    return (
      <Routes>
        <Route path="/" element={
          <ChatApp 
            token={token} 
            userEmail={userEmail}
            userName={userName}
            onLogout={handleLogout}
            onProfileClick={() => navigate('/profile')}
            onAdminClick={isAdmin ? () => navigate('/admin') : undefined}
          />
        } />
        <Route path="/profile" element={
          <ProtectedRoute isAuthenticated={!!token}>
            <ProfilePage onBack={() => { fetchUserProfile(); navigate('/'); }} userEmail={userEmail} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute isAdmin={isAdmin}>
            <AdminDashboard token={token} userEmail={userEmail} onBack={() => navigate('/')} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="*" element={<LoginScreen isLoading={isLoading} onLogin={handleLogin} onMidwayLogin={handleMidwayLogin} />} />
    </Routes>
  );
}

function App() {
  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
    return <MaintenancePage />;
  }
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;